import React, { useEffect, useRef } from 'react';
import Nav from '../components/nav';
import SEO from '../components/SEO';
import AnchorLink from "react-anchor-link-smooth-scroll";
import "../styles/privacyPolicy.css";
import { baseUrl } from '../config';

export function PrivacyPolicy() {
    const contentRef = useRef(null);
    
    useEffect(() => {
        const checkContentHeight = () => {
            if (contentRef.current) {
                const contentHeight = contentRef.current.scrollHeight;
                document.documentElement.style.setProperty('--privacy-content-height', `${contentHeight}px`);
            }
        };
        
        checkContentHeight();
        window.addEventListener('resize', checkContentHeight);
        
        return () => window.removeEventListener('resize', checkContentHeight);
    }, []);

    return (
        <div className="min-h-screen bg-white privacy-policy-container">
            <SEO
              title="Privacy Policy"
              description="Read ProducerBuddy's privacy policy to understand how we collect, use, and protect your personal data."
              url="https://producerbuddy.pilomation.com/privacy-policy"
            />
            <Nav/>
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 privacy-policy-main">
                
                <div id="privacyPolicyPageWrapper" className="privacy-policy-wrapper">
                    {/* Navigation Sidebar - Now using position: sticky instead of fixed */}
                    <div id="privacyPolicyNav" className="privacy-policy-nav">
                        <h2 className="text-lg font-semibold mb-4 privacy-nav-title">Quick Navigation</h2>
                        <AnchorLink href="#privacy-data-collection" className="block text-blue-600 hover:text-blue-800 privacy-nav-link">1. Personal Data We Collect</AnchorLink>
                        <AnchorLink href="#privacy-data-usage" className="block text-blue-600 hover:text-blue-800 privacy-nav-link">2. How We Use Personal Data</AnchorLink>
                        <AnchorLink href="#privacy-data-sharing" className="block text-blue-600 hover:text-blue-800 privacy-nav-link">3. Reasons We Share Personal Data</AnchorLink>
                        <AnchorLink href="#privacy-data-control" className="block text-blue-600 hover:text-blue-800 privacy-nav-link">4. Access and Control</AnchorLink>
                        <AnchorLink href="#privacy-cookies" className="block text-blue-600 hover:text-blue-800 privacy-nav-link">5. Cookies</AnchorLink>
                        <AnchorLink href="#privacy-org-products" className="block text-blue-600 hover:text-blue-800 privacy-nav-link">6. Organization Products</AnchorLink>
                        <AnchorLink href="#privacy-children" className="block text-blue-600 hover:text-blue-800 privacy-nav-link">7. Children's Data</AnchorLink>
                        <AnchorLink href="#privacy-ai-features" className="block text-blue-600 hover:text-blue-800 privacy-nav-link">8. AI Features</AnchorLink>
                        <AnchorLink href="#privacy-enterprise" className="block text-blue-600 hover:text-blue-800 privacy-nav-link">9. Enterprise Solutions</AnchorLink>
                        <AnchorLink href="#privacy-tools" className="block text-blue-600 hover:text-blue-800 privacy-nav-link">10. Productivity Tools</AnchorLink>
                        <AnchorLink href="#privacy-search" className="block text-blue-600 hover:text-blue-800 privacy-nav-link">11. Search Capabilities</AnchorLink>
                        <AnchorLink href="#privacy-entertainment" className="block text-blue-600 hover:text-blue-800 privacy-nav-link">12. Entertainment Services</AnchorLink>
                        <AnchorLink href="#privacy-security" className="block text-blue-600 hover:text-blue-800 privacy-nav-link">13. Data Security</AnchorLink>
                        <AnchorLink href="#privacy-international" className="block text-blue-600 hover:text-blue-800 privacy-nav-link">14. International Transfers</AnchorLink>
                        <AnchorLink href="#privacy-changes" className="block text-blue-600 hover:text-blue-800 privacy-nav-link">15. Policy Changes</AnchorLink>
                        <AnchorLink href="#privacy-contact" className="block text-blue-600 hover:text-blue-800 privacy-nav-link">16. Contact Us</AnchorLink>
                    </div>

                    {/* Main Content */}
                    <div id="privacyPolicyContent" className="privacy-policy-content" ref={contentRef}>
                        
                        <section id="privacy-data-collection" className="privacy-policy-section">
                            <h2 className="text-2xl font-bold mb-4 text-gray-800 privacy-section-title">1. Personal Data We Collect</h2>
                            <p className="text-gray-700 mb-4 privacy-section-text">
                                Producer Buddy collects data through your interactions with our platform and services. Some of this data is provided directly by you, while other data is collected based on your usage, interactions, and experiences with our tools.
                            </p>
                            <ul className="list-disc pl-6 space-y-2 text-gray-700 privacy-list">
                                <li>Sign-Up Information: When you create an account, we collect details such as your name, email address, username and password.</li>
                                <li>Personal Information: This includes your name, email address and contact details provided when you subscribe to our services or contact support.</li>
                                <li>Location Data: Information like IP address and GPS location is collected to provide personalized, location-based services.</li>
                                <li>Profile Edits: Any changes or updates made to your profile, such as job roles and preferences.</li>
                                <li>Usage Data: We collect information about how you interact with the platform.</li>
                                <li>Messaging Data: If you use our messaging services, we collect encrypted communications data.</li>
                            </ul>
                        </section>

                        <section id="privacy-data-usage" className="privacy-policy-section">
                            <h2 className="text-2xl font-bold mb-4 text-gray-800 privacy-section-title">2. How We Use Personal Data</h2>
                            <p className="text-gray-700 mb-4 privacy-section-text">
                                At Producer Buddy, we use the data we collect to deliver an interactive and seamless experience.
                            </p>
                            <ul className="list-disc pl-6 space-y-2 text-gray-700 privacy-list">
                                <li>Provide Our Services: Updating, securing, troubleshooting, and offering support.</li>
                                <li>Improve and Develop: Enhancing our platform based on collected data.</li>
                                <li>Personalize Experience: Tailoring features and recommendations.</li>
                                <li>Marketing and Advertising: Sending promotional messages and relevant offers.</li>
                            </ul>
                        </section>

                        <section id="privacy-data-sharing" className="privacy-policy-section">
                            <h2 className="text-2xl font-bold mb-4 text-gray-800 privacy-section-title">3. Reasons We Share Personal Data</h2>
                            <p className="text-gray-700 mb-4 privacy-section-text">
                                We share your personal data with your consent or to fulfill transactions you request through Producer Buddy.
                            </p>
                            <ul className="list-disc pl-6 space-y-2 text-gray-700 privacy-list">
                                <li>With Trusted Affiliates and Partners: For platform enhancement and service delivery.</li>
                                <li>Legal Requirements: When required by law or legal processes.</li>
                                <li>Protection of Users: To maintain security and prevent fraud.</li>
                                <li>Business Protection: To protect rights and property of Pilomation.</li>
                            </ul>
                        </section>

                        <section id="privacy-data-control" className="privacy-policy-section">
                            <h2 className="text-2xl font-bold mb-4 text-gray-800 privacy-section-title">4. How to Access and Control Your Personal Data</h2>
                            <p className="text-gray-700 mb-4 privacy-section-text">
                                You can make choices about the collection and use of your personal data through various tools and methods.
                            </p>
                            <ul className="list-disc pl-6 space-y-2 text-gray-700 privacy-list">
                                <li>Opt Out of Personalized Advertising</li>
                                <li>Manage Communications Preferences</li>
                                <li>Access and Manage Your Data</li>
                                <li>Control Sign-Up Data</li>
                                <li>Disable Your Account</li>
                            </ul>
                        </section>

                        <section id="privacy-cookies" className="privacy-policy-section">
                            <h2 className="text-2xl font-bold mb-4 text-gray-800 privacy-section-title">5. Cookies and Similar Technologies</h2>
                            <p className="text-gray-700 mb-4 privacy-section-text">
                                Producer Buddy uses cookies and similar technologies to enhance your experience.
                            </p>
                            <ul className="list-disc pl-6 space-y-2 text-gray-700 privacy-list">
                                <li>Social Media Cookies: Show relevant social media content</li>
                                <li>Analytics Cookies: Understand platform usage</li>
                                <li>Advertising Cookies: Display relevant ads</li>
                                <li>Required Cookies: Essential platform functions</li>
                            </ul>
                        </section>

                        <section id="privacy-org-products" className="privacy-policy-section">
                            <h2 className="text-2xl font-bold mb-4 text-gray-800 privacy-section-title">6. Products Provided by Organization</h2>
                            <p className="text-gray-700 mb-4 privacy-section-text">
                                Access through an organization may affect how your data is handled:
                            </p>
                            <ul className="list-disc pl-6 space-y-2 text-gray-700 privacy-list">
                                <li>Organizational Use: Your organization controls and administers your account</li>
                                <li>Personal Use: You maintain control over your account and personal data</li>
                            </ul>
                        </section>

                        <section id="privacy-children" className="privacy-policy-section">
                            <h2 className="text-2xl font-bold mb-4 text-gray-800 privacy-section-title">7. Collection of Data from Children</h2>
                            <p className="text-gray-700 mb-4 privacy-section-text">
                                We collect and process children's data only with explicit parental consent.
                            </p>
                            <ul className="list-disc pl-6 space-y-2 text-gray-700 privacy-list">
                                <li>Parental Consent Required</li>
                                <li>Limited Data Collection</li>
                                <li>Restricted Features</li>
                                <li>Parental Access Rights</li>
                            </ul>
                        </section>

                        <section id="privacy-ai-features" className="privacy-policy-section">
                            <h2 className="text-2xl font-bold mb-4 text-gray-800 privacy-section-title">8. AI-Driven Features</h2>
                            <div className="space-y-4 text-gray-700 privacy-ai-features">
                                <div className="privacy-ai-category">
                                    <h3 className="font-semibold mb-2 text-gray-800 privacy-subsection-title">Pre-Production:</h3>
                                    <ul className="list-disc pl-6 space-y-2 privacy-list">
                                        <li>Script Breakdown and Analysis</li>
                                        <li>AI-Based Scheduling</li>
                                        <li>Budgeting Based on Industry Trends</li>
                                        <li>Casting Recommendations</li>
                                    </ul>
                                </div>
                                <div className="privacy-ai-category">
                                    <h3 className="font-semibold mb-2 text-gray-800 privacy-subsection-title">Production:</h3>
                                    <ul className="list-disc pl-6 space-y-2 privacy-list">
                                        <li>Resource Optimization</li>
                                        <li>Daily Progress Reporting</li>
                                        <li>Real-Time Issue Tracking</li>
                                    </ul>
                                </div>
                                <div className="privacy-ai-category">
                                    <h3 className="font-semibold mb-2 text-gray-800 privacy-subsection-title">Post-Production:</h3>
                                    <ul className="list-disc pl-6 space-y-2 privacy-list">
                                        <li>Editing Suggestions</li>
                                        <li>Visual Effects Automation</li>
                                        <li>Sound Design</li>
                                    </ul>
                                </div>
                            </div>
                        </section>

                        <section id="privacy-enterprise" className="privacy-policy-section">
                            <h2 className="text-2xl font-bold mb-4 text-gray-800 privacy-section-title">9. Enterprise and Developer Solutions</h2>
                            <p className="text-gray-700 mb-4 privacy-section-text">
                                Scalable solutions for large projects, including integration with existing production pipelines and access to developer tools and APIs.
                            </p>
                        </section>

                        <section id="privacy-tools" className="privacy-policy-section">
                            <h2 className="text-2xl font-bold mb-4 text-gray-800 privacy-section-title">10. Productivity Tools</h2>
                            <ul className="list-disc pl-6 space-y-2 text-gray-700 privacy-list">
                                <li>Daily progress tracking</li>
                                <li>Internal chat for team communication</li>
                                <li>AI-driven scheduling and reminders</li>
                                <li>24X7 AI chat support</li>
                                <li>Messaging application</li>
                                <li>Career portal</li>
                                <li>Blogs page</li>
                            </ul>
                        </section>

                        <section id="privacy-search" className="privacy-policy-section">
                            <h2 className="text-2xl font-bold mb-4 text-gray-800 privacy-section-title">11. Search and Browse Capabilities</h2>
                            <ul className="list-disc pl-6 space-y-2 text-gray-700 privacy-list">
                                <li>Search uploaded scripts and production data</li>
                                <li>Browse resources and casting options</li>
                                <li>AI-powered recommendations</li>
                            </ul>
                        </section>

                        <section id="privacy-entertainment" className="privacy-policy-section">
                            <h2 className="text-2xl font-bold mb-4 text-gray-800 privacy-section-title">12. Entertainment Services</h2>
                            <ul className="list-disc pl-6 space-y-2 text-gray-700 privacy-list">
                                <li>Pre-Production: Script analysis and casting</li>
                                <li>Production: Real-time tracking and resource management</li>
                                <li>Post-Production: AI-driven market analysis</li>
                            </ul>
                        </section>

                        <section id="privacy-security" className="privacy-policy-section">
                            <h2 className="text-2xl font-bold mb-4 text-gray-800 privacy-section-title">13. Data Security</h2>
                            <ul className="list-disc pl-6 space-y-2 text-gray-700 privacy-list">
                                <li>Encryption: All data encrypted in transit and at rest</li>
                                <li>Additional Security: Firewalls, secure APIs, regular audits</li>
                                <li>Personal data used only when required and requested</li>
                            </ul>
                        </section>

                        <section id="privacy-international" className="privacy-policy-section">
                            <h2 className="text-2xl font-bold mb-4 text-gray-800 privacy-section-title">14. International Data Transfers</h2>
                            <p className="text-gray-700 privacy-section-text">
                                Data may be processed outside your country of residence, with safeguards to ensure compliance with data protection laws.
                            </p>
                        </section>

                        <section id="privacy-changes" className="privacy-policy-section">
                            <h2 className="text-2xl font-bold mb-4 text-gray-800 privacy-section-title">15. Changes to the Privacy Policy</h2>
                            <p className="text-gray-700 privacy-section-text">
                                Updates will be communicated via email or platform notifications.
                            </p>
                        </section>

                        <section id="privacy-contact" className="privacy-policy-section mb-12">
                            <h2 className="text-2xl font-bold mb-4 text-gray-800 privacy-section-title">16. Contact Us</h2>
                            <p className="text-gray-700 privacy-section-text">
                                If you have any questions, reach us at:
                            </p>
                            <ul className="list-disc pl-6 mt-2 text-gray-700 privacy-contact-list">
                                <li>Email: support@producerbuddy.com</li>
                                <li>Address: Pilomation, [Office Address]</li>
                            </ul>
                        </section>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default PrivacyPolicy;
